import {
	Component,
	ViewEncapsulation,
	Input,
	Output,
	EventEmitter,
	ViewChild,
	ElementRef,
	ChangeDetectionStrategy
} from "@angular/core";
import { IdService } from "../../shared/index";
import { makeDateUtils } from "../utils/DateUtils";

import * as _moment from "moment";

@Component({
	selector: "rbc-datepicker-day",
	templateUrl: "./datepicker-day.component.html",
	styles: [],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatepickerDayComponent {
	@Input() showDayView: Boolean;
	@Input() selectedDate: Date;
	@Input() pageDate: Date;
	@Input() pageTimestamp: Number;
	@Input() fullMonthName: Boolean;
	@Input() allowedToShowView;
	@Input() disabledDates;
	@Input() highlighted: Object;
	@Input() calendarClass: any;
	@Input() calendarStyle: Object;
	@Input() translation: Object;
	@Input() isRtl: Boolean;
	@Input() mondayFirst: Boolean;
	@Input() id: String;
	@Input() useUtc: Boolean;

	@Output() selectedDisabled = new EventEmitter();
	@Output() selectDateEmit = new EventEmitter();
	@Output() showMonthCalendarEmit = new EventEmitter();
	@Output() changeMonthEmit = new EventEmitter();

	@ViewChild("dayContainer", { static: false }) dayContainer: ElementRef;
	@ViewChild("daySpan", { static: false }) daySpan;

	constructedDateUtils = makeDateUtils(this.useUtc);
	utils = this.constructedDateUtils;
	math = Math;

	constructor(private idService: IdService) {
		this.id = idService.generate();
	}

	@Input() dayCellContent = day => day.date;

	/**
	 * Returns an array of day names
	 */
	daysOfWeek() {
		if (this.mondayFirst) {
			const tempDays = this.translation["days"].slice();
			tempDays.push(tempDays.shift());
			return tempDays;
		}

		return this.translation["days"];
	}

	/**
	 * Returns the day number of the week less one for the first of the current month
	 * Used to show amount of empty cells before the first in the day calendar layout
	 */
	blankDays() {
		const d = this.pageDate;

		let dObj = this.useUtc
			? new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
			: new Date(
				d.getFullYear(),
				d.getMonth(),
				1,
				d.getHours(),
				d.getMinutes()
			);
		if (this.mondayFirst) {
			return this.utils.getDay(dObj) > 0
				? this.utils.getDay(dObj) - 1
				: 6;
		}
		return this.utils.getDay(dObj);
	}


	days() {
		const d = this.pageDate;
		let days = [];
		// set up a new date object to the beginning of the current 'page'
		let dObj = this.useUtc
			? new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
			: new Date(
				d.getFullYear(),
				d.getMonth(),
				1,
				d.getHours(),
				d.getMinutes()
			);
		let daysInMonth = this.utils.daysInMonth(
			this.utils.getFullYear(dObj),
			this.utils.getMonth(dObj)
		);
		for (let i = 0; i < daysInMonth; i++) {
			days.push({
				date: this.utils.getDate(dObj),
				timestamp: dObj.getTime(),
				isSelected: this.isSelectedDate(dObj),
				isDisabled: this.isDisabledDate(dObj),
				isHighlighted: this.isHighlightedDate(dObj),
				isHighlightStart: this.isHighlightStart(dObj),
				isHighlightEnd: this.isHighlightEnd(dObj),
				isToday: this.utils.compareDates(dObj, new Date()),
				isWeekend:
					this.utils.getDay(dObj) === 0 ||
					this.utils.getDay(dObj) === 6,
				isSaturday: this.utils.getDay(dObj) === 6,
				isSunday: this.utils.getDay(dObj) === 0
			});
			this.utils.setDate(dObj, this.utils.getDate(dObj) + 1);
		}
		return days;
	}

	/**
	 * Gets the name of the month the current page is on
	 */
	currMonthName() {
		const monthName = this.fullMonthName
			? this.translation["months"]
			: this.translation["monthsAbbr"];
		const x = this.translation;
		return this.utils.getMonthNameAbbr(
			this.utils.getMonth(this.pageDate),
			monthName
		);
	}

	/**
	 * Gets the name of the year that current page is on
	 */
	currYearName() {
		const yearSuffix = this.translation["yearSuffix"];
		return `${this.utils.getFullYear(this.pageDate)}${yearSuffix}`;
	}

	/**
	 * Is this translation using year/month/day format?
	 */
	isYmd() {
		return this.translation["ymd"] && this.translation["ymd"] === true;
	}

	/**
	 * Is the left hand navigation button disabled?
	 */
	isLeftNavDisabled() {
		return this.isRtl
			? this.isNextMonthDisabled(this.pageTimestamp)
			: this.isPreviousMonthDisabled(this.pageTimestamp);
	}

	/**
	 * Is the right hand navigation button disabled?
	 */
	isRightNavDisabled() {
		return this.isRtl
			? this.isPreviousMonthDisabled(this.pageTimestamp)
			: this.isNextMonthDisabled(this.pageTimestamp);
	}

	selectDate(date) {
		// setTimeout(() => { document.getElementById(this.id.replace("PickerDay", "")).focus(); });
		if (date.isDisabled) {
			this.selectedDisabled.emit(date);
			return false;
		}
		this.selectDateEmit.emit(date);
	}


	getPageMonth() {
		return this.utils.getMonth(this.pageDate);
	}

	/**
	 * Emit an event to show the month picker
	 */
	showMonthCalendar() {
		this.showMonthCalendarEmit.emit();
	}

	/**
	 * Change the page month
	 */
	changeMonth(incrementBy) {
		let date = this.pageDate;
		this.utils.setMonth(date, this.utils.getMonth(date) + incrementBy);
		this.changeMonthEmit.emit(date);
	}

	/**
	 * Decrement the page month
	 */
	previousMonth() {
		if (!this.isPreviousMonthDisabled()) {
			this.changeMonth(-1);
		}
	}

	/**
	 * Is the previous month disabled?
	 */
	isPreviousMonthDisabled(dates?) {
		if (!this.disabledDates || !this.disabledDates["to"]) {
			return false;
		}
		let d = this.pageDate;
		return (
			this.utils.getMonth(this.disabledDates["to"]) >=
			this.utils.getMonth(d) &&
			this.utils.getFullYear(this.disabledDates["to"]) >=
			this.utils.getFullYear(d)
		);
	}

	/**
	 * Increment the current page month
	 */
	nextMonth() {
		if (!this.isNextMonthDisabled()) {
			this.changeMonth(+1);
		}
	}

	/**
	 * Is the next month disabled?
	 */
	isNextMonthDisabled(dates?) {
		if (!this.disabledDates || !this.disabledDates["from"]) {
			return false;
		}
		let d = this.pageDate;
		return (
			this.utils.getMonth(this.disabledDates["from"]) <=
			this.utils.getMonth(d) &&
			this.utils.getFullYear(this.disabledDates["from"]) <=
			this.utils.getFullYear(d)
		);
	}

	/**
	 * Whether a day is selected
	 */
	isSelectedDate(dObj) {
		return (
			this.selectedDate &&
			this.utils.compareDates(this.selectedDate, dObj)
		);
	}

	/**
	 * Whether a day is disabled
	 */
	isDisabledDate(date) {
		let disabledDates = false;
		if (typeof this.disabledDates === "undefined") {
			return false;
		}

		if (typeof this.disabledDates["dates"] !== "undefined") {
			this.disabledDates["dates"].forEach(d => {
				if (this.utils.compareDates(date, d)) {
					disabledDates = true;
					return true;
				}
			});
		}
		if (typeof this.disabledDates["to"] !== "undefined" && this.disabledDates["to"] && date < this.disabledDates["to"]) {
			disabledDates = true;
		}
		if (typeof this.disabledDates["from"] !== "undefined" && this.disabledDates["from"] && date > this.disabledDates["from"].setHours(0,0,0,0)) {
			disabledDates = true;
		}
		if (typeof this.disabledDates["ranges"] !== "undefined") {
			this.disabledDates["ranges"].forEach(range => {
				if (typeof range.from !== "undefined" && range.from && typeof range["to"] !== "undefined" && range["to"]) {
					if (date < range["to"] && date > range.from) {
						disabledDates = true;
						return true;
					}
				}
			});
		}
		if (typeof this.disabledDates["days"] !== "undefined" && this.disabledDates["days"].indexOf(this.utils.getDay(date)) !== -1) {
			disabledDates = true;
		}
		if (typeof this.disabledDates["daysOfMonth"] !== "undefined" && this.disabledDates["daysOfMonth"].indexOf(this.utils.getDate(date)) !==-1) {
			disabledDates = true;
		}
		if (typeof this.disabledDates["customPredictor"] === "function" && this.disabledDates.customPredictor(date)) {
			disabledDates = true;
		}
		return disabledDates;
	}

	/**
	 * Whether a day is highlighted (only if it is not disabled already except when highlighted.includeDisabled is true)
	 */
	isHighlightedDate(date) {
		if (
			!(this.highlighted && this.highlighted["includeDisabled"]) &&
			this.isDisabledDate(date)
		) {
			return false;
		}

		let highlighted = false;

		if (typeof this.highlighted === "undefined") {
			return false;
		}

		if (typeof this.highlighted["dates"] !== "undefined") {
			this.highlighted["dates"].forEach(d => {
				if (this.utils.compareDates(date, d)) {
					highlighted = true;
					return true;
				}
			});
		}

		if (
			this.isDefined(this.highlighted["from"]) &&
			this.isDefined(this.highlighted["to"])
		) {
			highlighted =
				date >= this.highlighted["from"] && date <= this.highlighted["to"];
		}

		if (
			typeof this.highlighted["days"] !== "undefined" &&
			this.highlighted["days"].indexOf(this.utils.getDay(date)) !== -1
		) {
			highlighted = true;
		}

		if (
			typeof this.highlighted["daysOfMonth"] !== "undefined" &&
			this.highlighted["daysOfMonth"].indexOf(this.utils.getDate(date)) !==
			-1
		) {
			highlighted = true;
		}

		if (
			typeof this.highlighted["customPredictor"] === "function" &&
			this.highlighted["customPredictor(date)"]
		) {
			highlighted = true;
		}

		return highlighted;
	}

	dayClasses(day) {
		return {
			selected: day.isSelected,
			disabled: day.isDisabled,
			highlighted: day.isHighlighted,
			today: day.isToday,
			weekend: day.isWeekend,
			sat: day.isSaturday,
			sun: day.isSunday,
			"highlight-start": day.isHighlightStart,
			"highlight-end": day.isHighlightEnd
		};
	}

	/**
	 * Whether a day is highlighted and it is the first date
	 * in the highlighted range of dates
	 */
	isHighlightStart(date) {
		return (
			this.isHighlightedDate(date) &&
			this.highlighted["from"] instanceof Date &&
			this.utils.getFullYear(this.highlighted["from"]) ===
			this.utils.getFullYear(date) &&
			this.utils.getMonth(this.highlighted["from"]) ===
			this.utils.getMonth(date) &&
			this.utils.getDate(this.highlighted["from"]) ===
			this.utils.getDate(date)
		);
	}

	/**
	 * Whether a day is highlighted and it is the first date
	 * in the highlighted range of dates
	 */
	isHighlightEnd(date) {
		return (
			this.isHighlightedDate(date) &&
			this.highlighted["to"] instanceof Date &&
			this.utils.getFullYear(this.highlighted["to"]) ===
			this.utils.getFullYear(date) &&
			this.utils.getMonth(this.highlighted["to"]) ===
			this.utils.getMonth(date) &&
			this.utils.getDate(this.highlighted["to"]) === this.utils.getDate(date)
		);
	}

	/**
	 * Helper
	 */
	isDefined(prop) {
		return typeof prop !== "undefined" && prop;
	}

	rows(n) {
		const rowNumber = Array.from({ length: n }, (v, k) => k + 1);

		return rowNumber;
	}
}
